import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import AsyncPaginate from 'react-select-async-paginate';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import { buildNameString, getAsyncLookupOptionValue } from 'services/utils';
import { reactRefType } from 'types/common';
import { staffMemberType } from 'types/staffMember';
import { apolloClientType } from 'types/apollo';
import { components } from 'react-select';
import styles from 'scss/main.scss';
import { getStaffMembers, getStaffMember } from './queries.gql';

const customSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? styles.colorGreyUltraLight : styles.colorWhite,
    color: 'inherit',
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '4 0',
    color: 'rgba(0, 0, 0, 0.54)',
  }),
  clearIndicator: provided => ({
    ...provided,
    padding: 4,
    color: 'rgba(0, 0, 0, 0.54)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '6px 0',
  }),
  input: provided => ({
    ...provided,
    margin: 0,
  }),
  singleValue: provided => ({
    ...provided,
    marginLeft: 0,
    marginRight: 0,
  }),
  control: (provided, state) => {
    const focusedStyle = state.isFocused ? styles.colorOffWhite : styles.colorWhite;
    return {
      'display': 'flex',
      'backgroundColor': state.hasValue ? styles.colorWhite : focusedStyle,
      '&:hover': {
        cursor: 'pointer',
      },
      '&:before': {
        'left': 0,
        'right': 0,
        'bottom': 0,
        'content': '"\\00a0"',
        'position': 'absolute',
        'transition': 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        'border-bottom': '1px solid',
        'borderBottomWidth': state.isFocused ? '2px' : '1px',
        'borderBottomColor': state.isFocused ? styles.colorHighlight : 'rgba(0, 0, 0, 0.42)',
        'pointer-events': 'none',
      },
    };
  },
  menu: provided => ({
    ...provided,
    marginTop: 0,
    paddingTop: 4,
    paddingBottom: 4,
  }),
};

const CustomClearIndicator = (props) => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props)}
    >
      <CloseIcon />
    </div>
  );
};

CustomClearIndicator.propTypes = {
  getStyles: PropTypes.func.isRequired,
  innerProps: PropTypes.shape({
    ref: reactRefType,
  }),
};

const CustomDropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <ArrowDropDownIcon />
  </components.DropdownIndicator>
);

class StaffMemberLookupContainer extends Component {
  static propTypes = {
    client: apolloClientType.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, staffMemberType]),
    label: PropTypes.string,
    organisationalUnits: PropTypes.arrayOf(PropTypes.string.isRequired),
    isSearchable: PropTypes.bool,
    disabled: PropTypes.bool,
    cacheUniq: PropTypes.string,
    onChange: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isSearchable: true,
  }

  constructor(props) {
    super(props);
    this.state = {};
  }

  getStaffMemberById = async (id) => {
    const { client, onChange } = this.props;

    const { data } = await client.query({
      query: getStaffMember,
      variables: {
        id,
      },
    });
    onChange(data.staffMember);
  }

  getStaffMembers = async (term, prevResults, prevMetadata) => {
    const { client, organisationalUnits } = this.props;

    const lastCursor = (prevMetadata && prevMetadata.lastCursor) ? prevMetadata.lastCursor : null;

    const data = await client.query({
      query: getStaffMembers,
      variables: {
        pagination: {
          first: 15,
          after: lastCursor,
        },
        term: (term !== '') ? term : null,
        organisationalUnits,
      },
    });
    const { edges, pageInfo } = data.data.staffMembers;

    const newResults = edges.map(({ node }) => ({
      label: buildNameString(node.name),
      value: node,
    }));

    return {
      options: newResults,
      hasMore: pageInfo.hasNextPage,
      additional: {
        lastCursor: pageInfo.endCursor,
      },
    };
  }

  render() {
    const {
      label, value: propValue, isSearchable, disabled, cacheUniq, onChange,
    } = this.props;

    if (typeof propValue === 'string') {
      this.getStaffMemberById(propValue);
    }

    const value = propValue ? {
      label: buildNameString(propValue.name),
      value: propValue,
    } : null;

    return (
      <AsyncPaginate
        id="staffMemberSelect"
        value={value}
        onChange={changedValue => onChange(changedValue && changedValue.value)}
        placeholder={label}
        loadOptions={this.getStaffMembers}
        getOptionValue={option => getAsyncLookupOptionValue(option)}
        debounceTimeout={500}
        isDisabled={disabled}
        isClearable
        isSearchable={isSearchable}
        cacheUniq={cacheUniq}
        styles={customSelectStyles}
        components={{
          ClearIndicator: CustomClearIndicator,
          DropdownIndicator: CustomDropdownIndicator,
        }}
      />
    );
  }
}

export default withApollo(StaffMemberLookupContainer);
